/**
 * Musical helpers for Track A — pleasant, scale-bound sonification.
 *
 * Continuous pads only: no noise buffers, no percussive grains, no
 * hard pitch quantize. Features glide along an A-minor pentatonic
 * so clump / membership / kinematics read as harmony, not static.
 */

/** A minor pentatonic (museum-safe, no harsh leading tones). Degrees in semis from A. */
export const PENTATONIC_SEMIS = [0, 3, 5, 7, 10] as const;

/** Concert A4 reference. */
export const A4_HZ = 440;

export function clamp01(x: number): number {
  return Math.max(0, Math.min(1, Number.isFinite(x) ? x : 0));
}

/** MIDI note → Hz. */
export function midiToHz(midi: number): number {
  return A4_HZ * Math.pow(2, (midi - 69) / 12);
}

/**
 * Continuous pentatonic walk: t∈[0,1] maps smoothly along scale degrees
 * (lerp between neighbors — never stair-steps like Math.floor).
 * baseMidi is the floor of the range (e.g. 57 ≈ A3).
 */
export function scaleHz(
  t: number,
  baseMidi: number,
  octaves = 1.25,
  pitchAmount = 1,
): number {
  const span = Math.max(1, PENTATONIC_SEMIS.length * Math.max(0.5, octaves));
  // pitchAmount widens travel; floor of ~0.25 keeps a little melody even at 0
  const width = 0.25 + 0.75 * clamp01(pitchAmount);
  const u = clamp01(t) * width * (span - 1e-6);
  const i0 = Math.floor(u);
  const frac = u - i0;
  const midiAt = (idx: number): number => {
    const i = Math.max(0, idx);
    const oct = Math.floor(i / PENTATONIC_SEMIS.length);
    const deg =
      PENTATONIC_SEMIS[
        ((i % PENTATONIC_SEMIS.length) + PENTATONIC_SEMIS.length) %
          PENTATONIC_SEMIS.length
      ]!;
    return baseMidi + oct * 12 + deg;
  };
  const a = midiAt(i0);
  const b = midiAt(i0 + 1);
  // Interpolate in log-frequency (equal temperament cents feel)
  const hzA = midiToHz(a);
  const hzB = midiToHz(b);
  return hzA * Math.pow(hzB / hzA, clamp01(frac));
}

/**
 * Soft equal-power-ish gain curve. Quiet clouds stay a murmur;
 * busy ones never slam into the ceiling.
 */
export function softGain(level: number, ceiling = 0.14): number {
  const x = clamp01(level);
  // Smoothstep — ease-in so level changes feel breath-like
  return ceiling * x * x * (3 - 2 * x);
}

/**
 * Consonant interval above a root (equal temperament).
 * semis may be fractional for gentle open/close of voicings.
 */
export function intervalHz(rootHz: number, semis: number): number {
  return rootHz * Math.pow(2, semis / 12);
}

/**
 * Long musical smoothing time-constants (seconds) for setTargetAtTime.
 * Longer = more pad-like; avoids the abrupt “tick” of short ramps.
 */
export const TAU = {
  gain: 0.38,
  pitch: 0.55,
  filter: 0.45,
  chord: 0.72,
} as const;
